import React from 'react';
import { 
  AppBar, 
  Toolbar, 
  Typography, 
  IconButton, 
  Avatar, 
  Menu, 
  MenuItem,
  ListItemIcon,
  ListItemText,
  Divider 
} from '@mui/material';
import * as Icons from '@mui/icons-material';
import { useTheme } from '@mui/material';

/**
 * Mobile header component with app title and user profile menu
 * 
 * @param {Object} props
 * @param {Object} props.user - User object
 * @param {Object} props.anchorEl - Anchor element for menu
 * @param {Function} props.handleMenuOpen - Function to handle menu open
 * @param {Function} props.handleMenuClose - Function to handle menu close
 * @param {Function} props.handleDarkModeToggle - Function to toggle dark mode
 * @param {Function} props.handleUnitPreferenceToggle - Function to toggle unit preference
 * @param {Function} props.handleLogout - Function to handle logout
 * @param {string} props.unitPreference - Current unit preference ('imperial' or 'metric')
 */
const MobileHeader = ({
  user,
  anchorEl,
  handleMenuOpen,
  handleMenuClose,
  handleDarkModeToggle,
  handleUnitPreferenceToggle,
  handleLogout,
  unitPreference
}) => {
  const theme = useTheme();

  return (
    <AppBar 
      position="sticky" 
      color="default" 
      elevation={1}
      sx={{
        top: 0,
        borderBottom: 1,
        borderColor: 'divider',
        backgroundColor: theme.palette.background.paper,
      }}
    >
      <Toolbar sx={{ minHeight: '56px', px: 2, justifyContent: 'space-between' }}>
        <Typography
          variant="h6"
          sx={{
            fontWeight: 600,
            fontSize: '1.25rem',
            fontFamily: 'Montserrat, Roboto, sans-serif',
          }}
        >
          Trove
        </Typography>
        
        {user && (
          <IconButton onClick={handleMenuOpen} size="small">
            <Avatar 
              src={user.photoURL} 
              alt={user.displayName}
              sx={{ width: 32, height: 32 }}
            >
              {user.displayName?.[0] || <Icons.Person />}
            </Avatar>
          </IconButton>
        )}
        
        <Menu
          anchorEl={anchorEl}
          open={Boolean(anchorEl)}
          onClose={handleMenuClose}
          anchorOrigin={{
            vertical: 'bottom',
            horizontal: 'right',
          }}
          transformOrigin={{
            vertical: 'top',
            horizontal: 'right',
          }}
        >
          <MenuItem onClick={handleDarkModeToggle}>
            <ListItemIcon>
              {theme.palette.mode === 'dark' ? <Icons.Brightness7 /> : <Icons.Brightness4 />}
            </ListItemIcon>
            <ListItemText>
              {theme.palette.mode === 'dark' ? 'Light Mode' : 'Dark Mode'}
            </ListItemText>
          </MenuItem>
          <MenuItem onClick={handleUnitPreferenceToggle}>
            <ListItemIcon>
              {unitPreference === 'imperial' ? <Icons.Scale /> : <Icons.SquareFoot />}
            </ListItemIcon>
            <ListItemText>
              {unitPreference === 'imperial' ? 'Metric Units (ml)' : 'Imperial Units (oz)'}
            </ListItemText>
          </MenuItem>
          <Divider />
          <MenuItem onClick={handleLogout}>
            <ListItemIcon>
              <Icons.ExitToApp />
            </ListItemIcon>
            <ListItemText>Sign Out</ListItemText>
          </MenuItem>
        </Menu>
      </Toolbar>
    </AppBar>
  );
};

export default MobileHeader; 